import { Bar } from '../Skeleton/primitives';
import { LoadingSpinnerIcon } from '../icons/LoadingSpinnerIcon';
import { useTranslation } from '../../hooks/useTranslation';
import { FrontEmailLink } from './FrontEmailLink';

// Shown in place of the events section while the AI is extracting the order from
// the email. The order shell (customer, salesperson) is already rendered above;
// only the events area waits. useOrder polls until extraction_status leaves
// "processing", then the real events replace this.
export function ExtractionLoadingCard() {
  const { t } = useTranslation();

  return (
    <div
      data-testid="extraction-loading"
      role="status"
      aria-busy="true"
      aria-label={t('order.extraction.loading_label', 'Reading the email and building the draft')}
      className="space-y-4"
    >
      <div className="flex items-center gap-3 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter px-4 py-3">
        <LoadingSpinnerIcon className="size-4 shrink-0 text-off-black" />
        <div>
          <p className="font-inter text-sm font-semibold leading-normal text-off-black">
            {t('order.extraction.loading_title', 'Reading the email & matching the menu…')}
          </p>
          <p className="font-inter text-xs leading-normal text-grey">
            {t('order.extraction.loading_subtitle', 'Usually 5–15s. The events fill in automatically — no need to refresh.')}
          </p>
        </div>
      </div>

      <SkeletonEventCard />
      <SkeletonEventCard />
    </div>
  );
}

function SkeletonEventCard() {
  return (
    <div className="space-y-4 rounded-input border border-dinnerplate-white bg-off-white p-6">
      <div className="flex items-center gap-3">
        <Bar className="h-6 w-24 rounded-xs" />
        <Bar className="h-6 w-40 rounded-xs" />
        <Bar className="h-6 w-16 rounded-xs" />
      </div>
      <Bar className="h-4 w-3/5 rounded-xs" />
      <div className="space-y-2">
        <Bar className="h-4 w-1/2 rounded-xs" />
        <Bar className="h-4 w-2/5 rounded-xs" />
        <Bar className="h-4 w-1/2 rounded-xs" />
      </div>
    </div>
  );
}

// What the extraction wants the rep to know: dishes it couldn't match to the
// menu, events it couldn't build, or why it failed. Read-only, rendered verbatim
// (one note per line); absent for orders with nothing to flag. Headed by a link
// back to the Front email the notes came from.
export function ExtractionNotesCard({
  notes,
  conversationId,
}: {
  notes: string;
  conversationId?: string | null;
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="extraction-notes"
      role="note"
      className="rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter px-4 py-3"
    >
      {conversationId && (
        <div className="mb-1 flex items-center gap-1.5">
          <h3 className="font-inter text-sm font-semibold leading-tight-sm text-off-black">
            {t('front_email.drafted_from')}
          </h3>
          <FrontEmailLink conversationId={conversationId} />
        </div>
      )}
      <p className="whitespace-pre-line font-inter text-sm leading-normal text-off-black">
        {notes}
      </p>
    </div>
  );
}

// Extraction failed: the draft exists but is empty. Shown above the normal events
// section so the rep can still build the order by hand.
export function ExtractionFailedNotice() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-input border border-chilli-red-light bg-chilli-blush px-4 py-3"
    >
      <p className="font-inter text-sm leading-normal text-off-black">
        {t(
          'order.extraction.failed',
          "Couldn't read this email automatically — add the events below by hand.",
        )}
      </p>
    </div>
  );
}
